import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowRightLeft,
  Bell,
  BellOff,
  Check,
  Gauge,
  Link2,
  MessageSquareReply,
  Play,
  Stamp,
  Trash2,
  Undo2,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { useTicketsStore } from '@/features/tickets';
import { useMassIncidentUi } from '@/features/mass-incidents';
import {
  useMuteStore,
  useTicketMute,
  useTicketMuteBusy,
} from '@/features/notifications/model/mute-store';
import { ticketsApi } from '@/shared/api/tickets';
import {
  apiErrorText,
  approvalCommitteesApi,
  massIncidentsApi,
  supportGroupsApi,
  tasksApi,
  type CommitteeBrief,
  type KpiComplexityLevel,
  type SupportGroup,
  type Task,
  type TaskStatus,
  type UserSearchResult,
} from '@/shared/api';
import { isHandedOff } from '@/pages/Sprints/task-flow';
import { pluralRu } from '@/shared/lib/plural';
import { transitionDropsApprovals } from './approval-drop';
import { APPROVAL_BLOCKED_STATUSES, isTicketFinal, ticketStatusLabel, GroupIcon, MassIncidentIcon } from '@/shared/config';
import {
  parseApprovalTransition,
  type AllowedTransition,
  type TicketApproval,
  type TicketDetailData,
} from '@/entities/ticket';
import {
  AsyncUserSelect,
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  Field,
  Input,
  Select,
  confirmDialog,
  promptDialog,
  toast,
} from '@/shared/ui';
import { committeeStructureText } from '@/shared/lib/approval-level';
import { useCopyToClipboard } from '@/shared/lib/useCopyToClipboard';
import type { TemplateContext } from '@/features/tickets/ui/TemplatePanel';
import { ApprovalTransitionDialog, type ApprovalSubmit } from './ApprovalTransitionDialog';
import { isBarCandidate, orderByPlacement } from './transition-placement';
import { ShareToChatDialog } from '@/features/messenger';
import { Share2 } from 'lucide-react';
import { useAuthStore } from '@/features/auth';
import { TransitionDialog } from './TransitionDialog';
import styles from '../TicketDetail.module.css';

export type ActionGroup = 'own' | 'status' | 'people' | 'approval' | 'service';

export const ACTION_GROUP_ORDER: ActionGroup[] = [
  'own',
  'status',
  'people',
  'approval',
  'service',
];

export const ACTION_GROUP_LABELS: Record<ActionGroup, string | null> = {
  own: null,
  status: 'Статус',
  people: 'Люди',
  approval: 'Согласование',
  service: 'Сервис',
};

export interface TicketAction {
  key: string;
  label: string;
  icon?: React.ReactNode;
  group: ActionGroup;
  bar?: boolean;
  danger?: boolean;
  disabled?: boolean;
  ariaDisabled?: boolean;
  hint?: string;
  active?: boolean;
  run: () => void;
}

export interface HeldTask {
  id: string;
  key: string;
  title: string;
  statusLabel: string;
}



type PeopleModal = 'assignee' | 'participant' | 'group';

export interface UseTicketActionsArgs {
  ticket: TicketDetailData;
  canTake: boolean;
  canWithdraw: boolean;
  transitions: AllowedTransition[];
  templateContext: TemplateContext | null;
  canAssign: boolean;
  canAddParticipant: boolean;
  canReassignGroup: boolean;
  canRequestApproval: boolean;
  frozen: boolean;
  isAgent: boolean;
  isAdmin: boolean;
  canRateComplexity: boolean;
  isAssigneeOfTicket: boolean;
  complexityLevels: KpiComplexityLevel[];
  myApproval: TicketApproval | null;
  onApprove: (approvalId: string, comment: string) => void;
  onReject: (approvalId: string, comment: string) => void;
  onApprovalCreated: (approval: TicketApproval) => void;
  onRoundStarted: () => void;
  onDeleted: () => void;
}

export function useTicketActions({
  ticket,
  canTake,
  canWithdraw,
  transitions,
  templateContext,
  canAssign,
  canAddParticipant,
  canReassignGroup,
  canRequestApproval,
  frozen,
  isAgent,
  isAdmin,
  canRateComplexity,
  isAssigneeOfTicket,
  complexityLevels,
  myApproval,
  onApprove,
  onReject,
  onApprovalCreated,
  onRoundStarted,
  onDeleted,
}: UseTicketActionsArgs): { actions: TicketAction[]; dialogs: React.ReactNode; heldByTasks: HeldTask[] } {
  const id = ticket.id;
  const status = ticket.status;
  const fetchTicket = useTicketsStore((s) => s.fetchTicket);
  const takeTicket = useTicketsStore((s) => s.takeTicket);
  const refreshTicket = useCallback(() => fetchTicket(id), [fetchTicket, id]);

  const canSeeTasks = useAuthStore((s) => s.hasPermission)('view_sprints');
  const [ticketTasks, setTicketTasks] = useState<Task[]>([]);
  const [taskStatusDict, setTaskStatusDict] = useState<TaskStatus[]>([]);
  useEffect(() => {
    if (!canSeeTasks) {
      setTicketTasks([]);
      return;
    }
    let alive = true;
    void Promise.all([tasksApi.list({ ticketId: id }), tasksApi.statuses()])
      .then(([list, dict]) => {
        if (!alive) return;
        setTicketTasks(list);
        setTaskStatusDict(dict);
      })
      .catch(() => alive && setTicketTasks([]));
    return () => {
      alive = false;
    };
  }, [id, canSeeTasks]);

  const openTasks = useMemo<HeldTask[]>(
    () =>
      ticketTasks
        .filter((t) => !isHandedOff(taskStatusDict.find((s) => s.key === t.status)))
        .map((t) => ({
          id: t.id,
          key: t.key,
          title: t.title,
          statusLabel: taskStatusDict.find((s) => s.key === t.status)?.label ?? t.status,
        })),
    [ticketTasks, taskStatusDict],
  );

  const [takeLoading, setTakeLoading] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const [pendingTransition, setPendingTransition] = useState<AllowedTransition | null>(null);
  const [dialogTransition, setDialogTransition] = useState<AllowedTransition | null>(null);
  const [openSeq, setOpenSeq] = useState(0);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const [peopleModal, setPeopleModal] = useState<PeopleModal | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const canShareToChat = useAuthStore((s) => s.hasPermission)('view_tickets');
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [supportGroups, setSupportGroups] = useState<SupportGroup[]>([]);

  const [approvalOpen, setApprovalOpen] = useState(false);
  const [committeeOpen, setCommitteeOpen] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [subject, setSubject] = useState('');
  const [showChat, setShowChat] = useState(true);
  const [showAttachments, setShowAttachments] = useState(true);
  const [committees, setCommittees] = useState<CommitteeBrief[]>([]);
  const [committeeId, setCommitteeId] = useState('');

  const [complexityBusy, setComplexityBusy] = useState(false);
  const [incidentLoading, setIncidentLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const openIncidentPicker = useMassIncidentUi((s) => s.openPicker);
  const openIncidentPanel = useMassIncidentUi((s) => s.openPanel);
  const openIncidentReply = useMassIncidentUi((s) => s.openReply);

  const { copy } = useCopyToClipboard();
  const loadMute = useMuteStore((s) => s.load);
  const toggleMute = useMuteStore((s) => s.toggle);
  const muteEntry = useTicketMute(id);
  const muteBusy = useTicketMuteBusy(id);

  useEffect(() => {
    void loadMute(id);
  }, [id, loadMute]);

  useEffect(() => {
    if (!canReassignGroup) return;
    supportGroupsApi
      .listBrief()
      .then((res) => setSupportGroups(res.data ?? []))
      .catch(() => setSupportGroups([]));
  }, [canReassignGroup]);

  useEffect(() => {
    if (!committeeOpen || committees.length > 0) return;
    approvalCommitteesApi
      .listBrief()
      .then((res) => {
        setCommittees(res.data);
        setCommitteeId((prev) => prev || res.data[0]?.id || '');
      })
      .catch((err) => toast.error(apiErrorText(err, 'Не удалось загрузить комитеты')));
  }, [committeeOpen, committees.length]);

  const handleTake = async () => {
    if (takeLoading) return;
    setTakeLoading(true);
    try {
      await takeTicket(id);
      refreshTicket();
    } finally {
      setTakeLoading(false);
    }
  };

  const handleWithdraw = async () => {
    const ok = await confirmDialog({
      title: 'Отозвать заявку?',
      description:
        'Заявка перейдёт в статус «Отозвана»: работа по ней прекратится, вернуть её будет нельзя.',
      confirmLabel: 'Отозвать',
      danger: true,
    });
    if (!ok) return;
    setWithdrawLoading(true);
    try {
      await ticketsApi.withdrawTicket(id);
      refreshTicket();
    } catch (err) {
      toast.error(apiErrorText(err, 'Ошибка отзыва заявки'));
    } finally {
      setWithdrawLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string, inputs?: Record<string, unknown>) => {
    setStatusChangeLoading(true);
    setDialogError(null);
    try {
      const res = await ticketsApi.update(id, { status: newStatus, inputs });
      if (res.data) refreshTicket();
      setPendingTransition(null);
    } catch (err) {
      const text = apiErrorText(err, 'Ошибка смены статуса');
      if (pendingTransition) setDialogError(text);
      else toast.error(text);
    } finally {
      setStatusChangeLoading(false);
    }
  };

  const runTransition = (t: AllowedTransition) => {
    if (t.inputs?.length) {
      setDialogError(null);
      setDialogTransition(t);
      setPendingTransition(t);
      setOpenSeq((n) => n + 1);
    } else {
      void handleStatusChange(t.to);
    }
  };

  const closeTransitionDialog = () => {
    if (statusChangeLoading) return;
    setPendingTransition(null);
    setDialogError(null);
  };

  const pendingApprovals = useMemo(
    () => (ticket.approvals ?? []).filter((a) => a.status === 'pending'),
    [ticket.approvals],
  );
  const dropsApprovals = (to: string) =>
    transitionDropsApprovals(status, to, pendingApprovals.length, isTicketFinal(ticket));

  const confirmDropApprovals = (t: AllowedTransition) =>
    confirmDialog({
      title: t.label ?? ticketStatusLabel(t.to),
      description: `${
        pendingApprovals.length === 1
          ? 'Одно согласование ещё ждёт решения'
          : `${pendingApprovals.length} ${pluralRu(pendingApprovals.length, 'согласование', 'согласования', 'согласований')} ещё ждут решения`
      }. При переходе в «${ticketStatusLabel(t.to)}» они будут сняты — согласующие уже не смогут ответить, а вернуть их можно только новым запросом.`,
      items: pendingApprovals.map((a) =>
        [a.approver?.displayName ?? 'Согласующий', a.subject].filter(Boolean).join(' · '),
      ),
      Icon: AlertTriangle,
      tone: 'warning',
      confirmLabel: t.label ?? 'Продолжить',
    });

  const confirmForceFinish = async (t: AllowedTransition) => {
    const ok = await confirmDialog({
      title: t.label ?? ticketStatusLabel(t.to),
      description: `У заявки ${openTasks.length} ${
        openTasks.length === 1 ? 'незавершённая задача' : 'незавершённых задач'
      }. Они останутся на доске, но заявку держать перестанут.`,
      items: openTasks.map((task) => `${task.key} · ${task.title} — ${task.statusLabel}`),
      Icon: AlertTriangle,
      tone: 'warning',
      confirmLabel: t.label ?? 'Продолжить',
    });
    if (ok) runTransition(t);
  };

  const submitInputs = (inputs: Record<string, unknown>) => {
    if (!pendingTransition) return;
    void handleStatusChange(pendingTransition.to, Object.keys(inputs).length ? inputs : undefined);
  };

  const startRound = async (submit: Extract<ApprovalSubmit, { kind: 'round' }>) => {
    setStatusChangeLoading(true);
    setDialogError(null);
    try {
      await ticketsApi.startApprovalRound(id, submit.committeeId, submit.subject, submit.scope);
      refreshTicket();
      setPendingTransition(null);
    } catch (err) {
      setDialogError(apiErrorText(err, 'Не удалось запустить согласование'));
    } finally {
      setStatusChangeLoading(false);
    }
  };

  const submitApproval = (submit: ApprovalSubmit) => {
    if (submit.kind === 'round') void startRound(submit);
    else submitInputs(submit.inputs);
  };

  const runPeople = async (
    action: () => Promise<unknown>,
    successText: string,
    errorText: string,
  ) => {
    setPeopleLoading(true);
    try {
      const res = (await action()) as
        | { meta?: { statusUnchanged?: { message: string }; groupMoved?: boolean } }
        | undefined;
      const unchanged = res?.meta?.statusUnchanged;
      if (unchanged) toast.warning(unchanged.message, { duration: 8000 });
      else if (res?.meta?.groupMoved) toast.success(`${successText}; заявка передана его группе`);
      else toast.success(successText);
      setPeopleModal(null);
      refreshTicket();
    } catch (err) {
      toast.error(apiErrorText(err, errorText));
    } finally {
      setPeopleLoading(false);
    }
  };

  const handleAddAssignee = (u: UserSearchResult) =>
    runPeople(
      () => ticketsApi.addAssignee(id, u.id),
      `${u.displayName} — назначен(а) ответственным`,
      'Ошибка назначения',
    );
  const handleAddParticipant = (u: UserSearchResult) =>
    runPeople(
      () => ticketsApi.addParticipant(id, u.id),
      `${u.displayName} — добавлен(а) в чат`,
      'Ошибка добавления участника',
    );
  const assignedGroupId = ticket.assignedGroup?.id ?? null;
  const handleReassignGroup = (groupId: string) => {
    if (!groupId || groupId === assignedGroupId) return;
    const name = supportGroups.find((g) => g.id === groupId)?.name ?? 'группа';
    void runPeople(
      () => ticketsApi.reassignGroup(id, groupId),
      assignedGroupId ? `Заявка передана: ${name}` : `Заявка назначена: ${name}`,
      assignedGroupId ? 'Ошибка переназначения группы' : 'Ошибка назначения группы',
    );
  };

  const resetApprovalForm = () => {
    setSubject('');
    setShowChat(true);
    setShowAttachments(true);
  };

  const handleCreateApproval = async (approver: UserSearchResult) => {
    if (!subject.trim()) return;
    setApprovalLoading(true);
    try {
      const scope =
        showChat && showAttachments
          ? undefined
          : { canViewChat: showChat, canViewAttachments: showAttachments };
      const res = await ticketsApi.createApproval(id, subject.trim(), approver.id, scope);
      onApprovalCreated(res.data as TicketApproval);
      toast.success(`Согласование запрошено: ${approver.displayName}`);
      resetApprovalForm();
      setApprovalOpen(false);
      refreshTicket();
    } catch (err) {
      toast.error(apiErrorText(err, 'Ошибка создания согласования'));
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleStartCommittee = async () => {
    if (!committeeId) return;
    setApprovalLoading(true);
    try {
      await ticketsApi.startApprovalRound(id, committeeId, subject.trim() || undefined);
      const name = committees.find((c) => c.id === committeeId)?.name ?? 'Комитет';
      toast.success(`Запущено согласование: ${name}`);
      setSubject('');
      setCommitteeOpen(false);
      onRoundStarted();
      refreshTicket();
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось запустить согласование'));
    } finally {
      setApprovalLoading(false);
    }
  };

  const withComplexityBusy = async (action: () => Promise<unknown>) => {
    if (complexityBusy) return;
    setComplexityBusy(true);
    try {
      await action();
      refreshTicket();
    } finally {
      setComplexityBusy(false);
    }
  };

  const handleRequestComplexity = async (cls: number) => {
    const comment = await promptDialog({
      title: 'Запрос сложности',
      description: 'Комментарий к запросу (необязательно).',
      label: 'Комментарий',
      multiline: true,
      confirmLabel: 'Отправить',
    });
    if (comment === null) return;
    return withComplexityBusy(() =>
      ticketsApi.requestComplexity(id, cls, comment.trim() || undefined),
    );
  };

  const handleUnmarkMassIncident = async () => {
    setIncidentLoading(true);
    try {
      await massIncidentsApi.unmark(id);
      refreshTicket();
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось убрать заявку из сбоя'));
    } finally {
      setIncidentLoading(false);
    }
  };

  const handleDeleteTicket = async () => {
    setDeleteLoading(true);
    try {
      await ticketsApi.deleteTicket(id);
      onDeleted();
    } catch (err) {
      toast.error(apiErrorText(err, 'Ошибка удаления заявки'));
      setDeleteLoading(false);
    }
  };

  const handleRejectFromBar = async () => {
    if (!myApproval) return;
    const comment = await promptDialog({
      title: 'Отклонить согласование',
      description: 'Причина увидит тот, кто запросил согласование.',
      label: 'Причина отклонения',
      multiline: true,
      confirmLabel: 'Отклонить',
    });
    if (comment === null) return;
    onReject(myApproval.id, comment.trim());
  };

  const actions: TicketAction[] = [];

  actions.push({
    key: 'copy-link',
    label: 'Скопировать ссылку',
    icon: <Link2 size={14} aria-hidden />,
    group: 'own',
    run: () => {
      copy(`${window.location.origin}/tickets/view/${id}`);
      toast.success('Ссылка скопирована');
    },
  });

  if (canShareToChat) {
    actions.push({
      key: 'share-chat',
      label: 'Поделиться в чат',
      icon: <Share2 size={14} aria-hidden />,
      group: 'own',
      run: () => setShareOpen(true),
    });
  }

  if (muteEntry?.canMute) {
    actions.push({
      key: 'mute',
      label: muteEntry.muted ? 'Включить уведомления' : 'Приглушить уведомления',
      icon: muteEntry.muted ? <Bell size={14} aria-hidden /> : <BellOff size={14} aria-hidden />,
      group: 'own',
      disabled: muteBusy,
      active: muteEntry.muted,
      hint: muteEntry.muted
        ? 'Уведомления по заявке приходят молча — без звука, всплывашек и писем'
        : 'Уведомления по заявке будут приходить без звука, всплывашек и писем',
      run: () => void toggleMute(id),
    });
  }

  if (myApproval) {
    actions.push({
      key: 'approve',
      label: 'Согласовать',
      icon: <Check size={14} aria-hidden />,
      group: 'approval',
      bar: true,
      run: () => onApprove(myApproval.id, ''),
    });
    actions.push({
      key: 'reject',
      label: 'Отклонить',
      icon: <X size={14} aria-hidden />,
      group: 'approval',
      bar: true,
      run: () => void handleRejectFromBar(),
    });
  }

  if (canTake) {
    actions.push({
      key: 'take',
      label: takeLoading ? 'Берём…' : 'Взять в работу',
      icon: <Play size={14} aria-hidden />,
      group: 'status',
      bar: true,
      disabled: takeLoading,
      run: () => void handleTake(),
    });
  }

  const visibleTransitions = orderByPlacement(
    canWithdraw ? transitions.filter((t) => t.to !== 'withdrawn') : transitions,
  );

  for (const t of visibleTransitions) {
    const blocked = t.blocked;
    const forceFinish = t.to === 'rejected' || t.to === 'closed';
    actions.push({
      key: `transition:${t.to}`,
      label: t.label ?? ticketStatusLabel(t.to),
      group: 'status',
      bar: isBarCandidate(t),
      disabled: statusChangeLoading,
      ariaDisabled: Boolean(blocked),
      hint: blocked?.reason,
      run: () => {
        if (blocked) return;
        void (async () => {
          if (dropsApprovals(t.to) && !(await confirmDropApprovals(t))) return;
          if (forceFinish && openTasks.length > 0) {
            void confirmForceFinish(t);
            return;
          }
          runTransition(t);
        })();
      },
    });
  }

  if (canAssign) {
    actions.push({
      key: 'assignee',
      label: 'Назначить ответственного',
      icon: <UserPlus size={14} aria-hidden />,
      group: 'people',
      run: () => setPeopleModal('assignee'),
    });
  }
  if (canAddParticipant) {
    actions.push({
      key: 'participant',
      label: 'Добавить участника',
      icon: <Users size={14} aria-hidden />,
      group: 'people',
      run: () => setPeopleModal('participant'),
    });
  }
  if (canReassignGroup && supportGroups.length > 0) {
    actions.push({
      key: 'group',
      label: assignedGroupId ? 'Передать другой группе' : 'Назначить группу',
      icon: <GroupIcon size={14} aria-hidden />,
      group: 'people',
      bar: !assignedGroupId,
      hint: assignedGroupId
        ? undefined
        : 'Заявка ляжет в очередь группы и станет ей видна',
      run: () => setPeopleModal('group'),
    });
  }

  const approvalBlocked = APPROVAL_BLOCKED_STATUSES.includes(status) || isTicketFinal(ticket);
  const approvalBlockedHint = 'Заявка завершена — новое согласование добавить нельзя.';
  if (canRequestApproval) {
    actions.push({
      key: 'request-approval',
      label: 'Запросить согласование',
      icon: <Stamp size={14} aria-hidden />,
      group: 'approval',
      disabled: approvalBlocked,
      hint: approvalBlocked ? approvalBlockedHint : 'Один согласующий по предмету согласования',
      run: () => setApprovalOpen(true),
    });
    actions.push({
      key: 'request-committee',
      label: 'Согласование комитетом',
      icon: <Users size={14} aria-hidden />,
      group: 'approval',
      disabled: approvalBlocked,
      hint: approvalBlocked
        ? approvalBlockedHint
        : 'Многоуровневое согласование комитетом: кворум по уровням',
      run: () => setCommitteeOpen(true),
    });
  }

  const canTouchComplexity =
    isAgent &&
    complexityLevels.length > 0 &&
    !frozen &&
    status !== 'resolved' &&
    (canRateComplexity || isAssigneeOfTicket);
  if (canTouchComplexity) {
    for (const level of complexityLevels.filter(
      (l) => l.class !== (ticket.complexityClass ?? 1),
    )) {
      actions.push({
        key: `complexity:${level.class}`,
        label: canRateComplexity
          ? `Сложность: ${level.label}`
          : `Запросить сложность: ${level.label}`,
        icon: <Gauge size={14} aria-hidden />,
        group: 'service',
        disabled: complexityBusy,
        hint: `×${level.multiplier} к KPI`,
        run: () =>
          canRateComplexity
            ? void withComplexityBusy(() => ticketsApi.setComplexity(id, level.class))
            : void handleRequestComplexity(level.class),
      });
    }
  }

  const incidentId = ticket.massIncidentId;
  const pickIncident = (currentIncidentId?: string) =>
    openIncidentPicker({
      ticketIds: [id],
      categoryId: ticket.categoryId,
      subcategoryId: ticket.subcategoryId,
      currentIncidentId,
    });
  if (isAgent && !frozen && !incidentId) {
    actions.push({
      key: 'mass-incident',
      label: 'В массовый сбой…',
      icon: <MassIncidentIcon size={14} aria-hidden />,
      group: 'service',
      hint: 'Положить в действующий сбой или завести новый',
      run: () => pickIncident(),
    });
  }
  if (isAgent && !frozen && incidentId) {
    actions.push(
      {
        key: 'mass-incident-open',
        label: 'Открыть сбой',
        icon: <MassIncidentIcon size={14} aria-hidden />,
        group: 'service',
        hint: 'Какие заявки в сбое, «устранён», ответ всем',
        run: () => openIncidentPanel(incidentId),
      },
      {
        key: 'mass-reply',
        label: 'Ответить всем в сбое',
        icon: <MessageSquareReply size={14} aria-hidden />,
        group: 'service',
        hint: 'Ответить разом в отмеченные заявки сбоя',
        run: () => openIncidentReply(incidentId, id),
      },
      {
        key: 'mass-incident-move',
        label: 'Перенести в другой сбой…',
        icon: <ArrowRightLeft size={14} aria-hidden />,
        group: 'service',
        run: () => pickIncident(incidentId),
      },
      {
        key: 'mass-incident-unmark',
        label: 'Убрать из сбоя',
        icon: <X size={14} aria-hidden />,
        group: 'service',
        disabled: incidentLoading,
        run: () => void handleUnmarkMassIncident(),
      },
    );
  }

  if (canWithdraw) {
    actions.push({
      key: 'withdraw',
      label: withdrawLoading ? 'Отзываем…' : 'Отозвать заявку',
      icon: <Undo2 size={14} aria-hidden />,
      group: 'own',
      danger: true,
      disabled: withdrawLoading,
      hint: 'Отозванная заявка не подлежит дальнейшей обработке',
      run: () => void handleWithdraw(),
    });
  }
  if (isAdmin) {
    actions.push({
      key: 'delete',
      label: 'Удалить заявку',
      icon: <Trash2 size={14} aria-hidden />,
      group: 'service',
      danger: true,
      run: () => setDeleteConfirm(true),
    });
  }

  const approvalSpec = dialogTransition ? parseApprovalTransition(dialogTransition) : null;
  const dialogTitle = dialogTransition
    ? (dialogTransition.label ?? ticketStatusLabel(dialogTransition.to))
    : '';
  const dialogStatus = dialogTransition
    ? ticketStatusLabel(dialogTransition.to, dialogTransition.toLabel)
    : '';
  const selectedCommittee = committees.find((c) => c.id === committeeId);

  const dialogs = (
    <>
      <ShareToChatDialog
        open={shareOpen}
        refText={`#${ticket.number ?? ''}`}
        title={ticket.title ?? ''}
        onClose={() => setShareOpen(false)}
      />
      {approvalSpec ? (
        <ApprovalTransitionDialog
          key={openSeq}
          open={pendingTransition != null}
          ticketId={id}
          spec={approvalSpec}
          title={dialogTitle}
          loading={statusChangeLoading}
          error={dialogError}
          onClose={closeTransitionDialog}
          onConfirm={submitApproval}
          templateContext={templateContext}
        />
      ) : (
        <TransitionDialog
          key={openSeq}
          open={pendingTransition != null}
          transition={dialogTransition}
          title={dialogTitle}
          statusLabel={dialogStatus}
          loading={statusChangeLoading}
          error={dialogError}
          onClose={closeTransitionDialog}
          onConfirm={submitInputs}
          templateContext={templateContext}
        />
      )}

      <Dialog
        open={peopleModal === 'assignee' || peopleModal === 'participant'}
        onOpenChange={(open) => !open && setPeopleModal(null)}
      >
        <DialogContent>
          <DialogTitle>
            {peopleModal === 'assignee' ? 'Назначить ответственного' : 'Добавить участника'}
          </DialogTitle>
          <DialogDescription>
            {peopleModal === 'assignee'
              ? 'Инженер станет исполнителем заявки.'
              : 'Пользователь получит доступ к чату заявки.'}
          </DialogDescription>
          <div className={styles.reassignForm}>
            <AsyncUserSelect
              placeholder={
                peopleModal === 'assignee' ? 'Кого назначить...' : 'Кого добавить в чат...'
              }
              onSelect={peopleModal === 'assignee' ? handleAddAssignee : handleAddParticipant}
              disabled={peopleLoading}
              staffOnly={peopleModal === 'assignee'}
              excludeUserIds={
                peopleModal === 'assignee' && ticket.creatorId ? [ticket.creatorId] : undefined
              }
              excludeReason="автор заявки"
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={peopleModal === 'group'} onOpenChange={(open) => !open && setPeopleModal(null)}>
        <DialogContent>
          <DialogTitle>{assignedGroupId ? 'Передать другой группе' : 'Назначить группу'}</DialogTitle>
          <DialogDescription>
            {assignedGroupId
              ? 'Смена очереди/зоны ответственности — исполнители остаются.'
              : 'Заявка ляжет в очередь группы и станет ей видна. Исполнителя назначают отдельно — или инженер берёт заявку сам.'}
          </DialogDescription>
          <div className={styles.reassignForm}>
            <Field label="Группа поддержки">
              <Select
                placeholder="Выберите группу…"
                value={assignedGroupId ?? ''}
                onValueChange={handleReassignGroup}
                disabled={peopleLoading}
                options={supportGroups.map((g) => ({ value: g.id, label: g.name }))}
              />
            </Field>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={approvalOpen} onOpenChange={setApprovalOpen}>
        <DialogContent>
          <DialogTitle>Запросить согласование</DialogTitle>
          <DialogDescription>
            Укажите предмет и выберите согласующего. Видимость чата и вложений настраивается.
          </DialogDescription>
          <div className={styles.reassignForm}>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Предмет согласования"
            />
            <div className={styles.checkRow}>
              <Checkbox
                checked={showChat}
                onCheckedChange={(checked) => setShowChat(checked === true)}
                label="чат"
              />
              <Checkbox
                checked={showAttachments}
                onCheckedChange={(checked) => setShowAttachments(checked === true)}
                label="вложения"
              />
            </div>
            <AsyncUserSelect
              placeholder="Согласующий..."
              onSelect={handleCreateApproval}
              disabled={approvalLoading || !subject.trim()}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={committeeOpen} onOpenChange={setCommitteeOpen}>
        <DialogContent>
          <DialogTitle>Согласование комитетом</DialogTitle>
          <DialogDescription>
            Запросы уйдут сразу всем членам всех уровней. Заявка вернётся с итогом, когда каждый
            уровень наберёт свой кворум.
          </DialogDescription>
          <div className={styles.reassignForm}>
            {committees.length === 0 ? (
              <p className={styles.actionHint}>
                Комитетов нет — их создаёт администратор в разделе «Комитеты согласования».
              </p>
            ) : (
              <>
                <Field label="Комитет">
                  <Select
                    value={committeeId}
                    onValueChange={setCommitteeId}
                    options={committees.map((c) => ({ value: c.id, label: c.name }))}
                    placeholder="Выберите комитет"
                  />
                </Field>
                { }
                {selectedCommittee && (
                  <p className={styles.actionHint}>
                    {committeeStructureText(selectedCommittee.levels)}
                  </p>
                )}
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Предмет согласования (необязательно)"
                />
                <Button onClick={handleStartCommittee} disabled={approvalLoading || !committeeId}>
                  {approvalLoading ? 'Запускаем...' : 'Отправить комитету'}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <DialogContent>
          <DialogTitle>Удалить заявку?</DialogTitle>
          <DialogDescription>
            Заявка «{ticket.title}» будет удалена безвозвратно вместе с чатом и вложениями. Отменить
            это действие нельзя.
          </DialogDescription>
          <DialogFooter>
            <DialogClose render={<Button variant="ghost">Отмена</Button>} />
            <Button variant="destructive" onClick={handleDeleteTicket} disabled={deleteLoading}>
              {deleteLoading ? 'Удаление…' : 'Удалить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  return { actions, dialogs, heldByTasks: openTasks };
}
